import type * as React from 'react';
import { Helmet } from 'react-helmet-async';

import { XingletRouter } from '@xing-com/crate-core-router';
import type { Host, Xinglet } from '@xing-com/crate-xinglet';
import { XingletLoader } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';
import { useRuntime } from '@xing-com/crate-xinglet/internal';
import GlobalStyles from '@xing-com/global-styles';
import { useScrambledId } from '@xing-com/scrambled-id';
// eslint-disable-next-line monorepo/forbidden-imports
import { TopBar } from '@xing-com/top-bar';

import { NotFound } from './error-components';
import { LoggedInHub, LoggedOutHub } from './hubs';
import { LoadingSkeleton } from './loading-skeleton';
import { Route } from './route';
import { useRegisterExperiments } from './use-register-experiments';

const SetScrambledId: React.FC = () => {
  useScrambledId();
  return null;
};

const Shell: React.FC<React.PropsWithChildren> = ({ children }) => {
  useRegisterExperiments();

  const { config } = useRuntime();
  const { loginState } = config;

  return (
    <GlobalStyles>
      {loginState === 'LOGGED_OUT' ? (
        <LoggedOutHub>{children}</LoggedOutHub>
      ) : (
        <LoggedInHub loginState={loginState}>
          {children}
          <SetScrambledId />
        </LoggedInHub>
      )}
    </GlobalStyles>
  );
};

export default class RootXinglet implements Xinglet {
  public getComponent(
    host: Host,
    runtime: Runtime
  ): React.ComponentType<{
    topBar?: React.ComponentProps<typeof TopBar>;
  }> {
    if (!host.isServer) {
      setTimeout(() => {
        // NOTE: We preload error pages here as it would be too late in case of
        // a network outage
        runtime.loadXinglet('@xing-com/crate-communication-error-pages');
      }, 1000);
    }

    return ({ topBar }) => {
      if (topBar) return <TopBar {...topBar} />;

      const favicon = runtime.config.isPreview ? 'data:,' : '/favicon.ico';

      return (
        <XingletLoader name="@xing-com/crate-core-hops-environment">
          <Helmet>
            <title>XING</title>
            <link rel="icon" href={favicon} />
          </Helmet>
          <Shell>
            <XingletRouter
              Route={Route}
              NotFound={NotFound}
              fallback={<LoadingSkeleton />}
            />
          </Shell>
        </XingletLoader>
      );
    };
  }
}
